<template>
  <div class="job-manager-jobs">
    <v-container fluid>
      <!-- Header -->
      <v-row>
        <v-col cols="12">
          <h1 class="text-h4 mb-4">{{ $t('job-manager.jobs-title') }}</h1>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-row>
        <v-col cols="12">
          <job-filters @filter="applyFilters" :options="filterOptions" :loadingOptions="loadingFilterOptions" />
        </v-col>
      </v-row>

      <!-- Jobs Table -->
      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="jobs"
            :items-per-page="options.itemsPerPage"
            :loading="loading"
            :server-items-length="total"
            :options.sync="options"
            class="elevation-1"
          >
            <template v-slot:item.id="{ item }">
              <a href="#" @click.prevent="showJobDetails(item)">{{ item.id }}</a>
            </template>

            <template v-slot:item.createdAt="{ item }">
              {{ item.createdAt | formatDate }}
            </template>

            <template v-slot:item.duration="{ item }">
              {{ item.duration }}s
            </template>

            <template v-slot:item.type="{ item }">
              <v-chip
                small
                :color="item.type === 'workflow' ? 'blue' : 'green'"
                text-color="white"
              >
                {{ item.type }}
              </v-chip>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip
                small
                :color="getStatusColor(item.status)"
                text-color="white"
              >
                {{ item.status }}
              </v-chip>
              <v-chip
                v-if="hasFailedExec(item)"
                small
                color="red"
                outlined
                class="ml-1"
                text-color="red darken-2"
              >
                {{ $t('job-manager.with-retry') }}
              </v-chip>
            </template>

            <template v-slot:item.attempts="{ item }">
              <span v-if="item.max_attempts">{{ item.attempts }} / {{ item.max_attempts }}</span>
              <span v-else>{{ item.attempts }}</span>
            </template>

          </v-data-table>
        </v-col>
      </v-row>

      <!-- Job Details Modal -->
      <job-details-modal
        v-model="showModal"
        :job="selectedJob"
      />
    </v-container>
  </div>
</template>

<script>
import JobFilters from '../elements/JobFilters.vue';
import JobDetailsModal from '../modal/JobDetailsModal.vue';
import JobRepository from "@repository/JobRepository";

export default {
  name: 'Jobs',
  components: {
    JobFilters,
    JobDetailsModal
  },
  data() {
    return {
      loading: false,
      filters: {},
      jobs: [],
      total: 0,
      headers: [
        { text: this.$t('job-manager.created-at'), value: 'createdAt' },
        { text: this.$t('job-manager.id'), value: 'id' },
        { text: this.$t('job-manager.definition'), value: 'jobDefinition.name' },
        { text: this.$t('job-manager.type'), value: 'type' },
        { text: this.$t('job-manager.status'), value: 'status' },
        { text: this.$t('job-manager.duration'), value: 'duration' },
      ],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      filterOptions: {},
      loadingFilterOptions: false,
      showModal: false,
      selectedJob: null,
      // selectedJob: {
      //   // Sample job data for the modal
      //   id: '',
      //   definition: '',
      //   type: '',
      //   status: '',
      //   attempts: 0,
      //   max_attempts: 0,
      //   origin: '',
      //   user: '',
      //   created_at: '',
      //   duration: '',
      //   transitions: [
      //     {
      //       timestamp: '2023-05-15 14:30:00',
      //       status: 'pending',
      //       duration: '0s',
      //       message: 'Job created'
      //     },
      //     {
      //       timestamp: '2023-05-15 14:30:01',
      //       status: 'running',
      //       duration: '1s',
      //       message: 'Job started'
      //     },
      //     {
      //       timestamp: '2023-05-15 14:30:05',
      //       status: 'success',
      //       duration: '4s',
      //       message: 'Job completed successfully'
      //     }
      //   ],
      //   logs: [
      //     {
      //       type: 'stdout',
      //       timestamp: '2023-05-15 14:30:02',
      //       content: 'Processing started...'
      //     },
      //     {
      //       type: 'stdout',
      //       timestamp: '2023-05-15 14:30:04',
      //       content: 'Processing completed successfully.'
      //     }
      //   ],
      //   context: {
      //     user_id: 123,
      //     organization_id: 456
      //   }
      // }
    };
  },

  mounted() {
    this.loadOptions()
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.searchJobs();
      }
    },
    options: {
      deep: true,
      handler() {
        this.searchJobs();
      }
    }
  },

  methods: {
    applyFilters(filters) {
      this.filters = filters;
      // In a real application, this would trigger a data reload
    },
    showJobDetails(job) {
       this.selectedJob = this.cloneObj(job);
      this.showModal = true;
    },

    searchJobs() {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const offset = (page - 1) * itemsPerPage;
      const limit = itemsPerPage;
      const orderBy = Array.isArray(sortBy) && sortBy.length ? sortBy[0] : null;
      const orderDirection = Array.isArray(sortDesc) && sortDesc.length && sortDesc[0] === false ? 'asc' : 'desc';

      const payload = {
        offset,
        limit,
        orderBy,
        orderDirection,
        // Map filters to backend query
        users: this.filters.users || [],
        queues: this.filters.queues || [],
        types: this.filters.types || [],
        statuses: this.filters.statuses || [],
        statues: this.filters.statuses || [], // in case backend uses this key
        jobId: this.filters.jobId || null,
        jobDefinitionNames: this.filters.jobDefinitionNames || [],
        searchInContext: this.filters.searchInContext || null,
      };

      this.loading = true;
      JobRepository.jobs(payload).then(data => {
        this.total = data.total;
        // Normalize and enrich items to match table expectations
        this.jobs = (data.items || []).map(j => {
          const executions = Array.isArray(j.executions) ? j.executions : [];
          const lastExec = executions.length ? executions[executions.length - 1] : null;
          const duration = executions.reduce((sum, e) => sum + (e.duration || 0), 0);
          // Normalize status for display
          const status = (j.status || '').toString().toLowerCase();
          return {
            ...j,
            status,
            duration,
          };
        });
      }).finally(() => {
        this.loading = false;
      });
    },

    async loadOptions() {
      this.loadingFilterOptions = true;
      try {
        // Load options and job definitions in parallel
        const [optionsData, definitionsData] = await Promise.all([
          JobRepository.options(this.filters),
          JobRepository.jobDefinitions()
        ])

        // Get job definitions array from various response formats
        let jobDefinitions = []
        if (Array.isArray(definitionsData)) {
          jobDefinitions = definitionsData
        } else if (definitionsData && Array.isArray(definitionsData.items)) {
          jobDefinitions = definitionsData.items
        } else if (definitionsData && Array.isArray(definitionsData.definitions)) {
          jobDefinitions = definitionsData.definitions
        }

        // Map job definitions to the format expected by JobFilters
        const jobDefinitionNames = jobDefinitions.map(def => ({
          text: def.name,
          value: def.name
        }))

        // Merge options with job definitions
        this.filterOptions = {
          ...optionsData,
          jobDefinitionNames
        }
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.loadingFilterOptions = false;
      }
    },

    getStatusColor(status) {
      const colors = {
        pending: 'orange',
        running: 'blue',
        success: 'green',
        failed: 'red',
        cancelled: 'grey'
      };
      const key = (status || '').toString().toLowerCase();
      return colors[key] || 'grey';
    },

    retryJob(job) {
      // TODO: Implement retry action via JobRepository if available
      // Placeholder handler to avoid runtime errors and enable UI action
      this.$emit && this.$emit('retry-job', job);
    },

    cancelJob(job) {
      // TODO: Implement cancel action via JobRepository if available
      // Placeholder handler to avoid runtime errors and enable UI action
      this.$emit && this.$emit('cancel-job', job);
    },

    hasFailedExec(item) {
      const execs = item && Array.isArray(item.executions) ? item.executions : [];
      return execs.some(e => ((e && e.status) ? e.status.toString().toLowerCase() : '') === 'failed');
    },
  }
};
</script>

<style lang="scss" scoped>
.job-manager-jobs {
  padding: 20px;
}
</style>
